/**
 * Wave Z — 2D drawing & auxiliary geometry commands.
 * (docs/architecture/command-parity-plan.md)
 */

import {
    runInteraction, PT, PLoop, PN, PTxt, PK, PLoopK, rubberBand, requireSelection,
    entityToPolyline, solidEntity, curveEntity, makeEntity, dataNum, dataArr, dataArrs,
    nurbsCurveEntity, splineEntity,
} from './lib';
import { useCommandInteract } from '../commandInteract';
import { interpolateCurve, curveFromControlPoints } from '../../geometry/nurbs/curve';
import { strokeTextToMesh } from '../../geometry/nurbs/textFont';
import { undoableAdd, undoableModify, undoableReplace } from '../entityOps';
import { useAppStore } from '../../../state/store';
import {
    spiralPoints, triangulatePolygon, lineIntersection2, circleIntersections2,
} from '../geometryMath';
import { boxMesh } from '../meshFactory';
import type { Pt3 } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';
import type { KnotParameterization } from '../splineMath';
import { useGradientHatchDialogStore } from '../../../components/cad/GradientHatchDialog';
import { useTextStyleStore } from '../../../tools/cad/TextTools';
import { useSystemVarsStore } from '../../../state/useSystemVarsStore';
import { useMlineStyleStore } from '../../../state/commandAuxStores';
import { notify } from '../../../utils/notify';

/** Triangulate a simple polygon into a flat mesh payload. */
function flatMesh(pts: Pt3[]): { vertices: number[][]; indices: number[][] } {
    return {
        vertices: pts.map((p) => [p[0], p[1], p[2] ?? 0]),
        indices: triangulatePolygon(pts),
    };
}

export const drawRunners: RunnerMap = {
    // SPLINE (Faz 7.2): fit-point mode with the OCS option set. Object
    // converts a picked curve/polyline through its resolved geometry,
    // Knots sets the KNOTS parameterization (Chord/Sqrt/Uniform), Close
    // commits the collected points as a closed loop.
    'draw.spline': () => startSplineFit(),

    // SPLINECV (Faz 7.2): control-vertex mode with the Degree option
    // (CV mode only; clamped to points.len()-1 like OCS).
    'draw.splinecv': () => startSplineCv(),

    // RECTANGLE with 2D options: bare RECT activates the interactive
    // tool; `RECT D <w>,<h>` drops a width-x-height rectangle from a corner
    // click; FILLETRADIUS rounds the corners (the tool path reads it too).
    'draw.rectangle': (args) => {
        const d = (args?.[0] ?? '').toUpperCase();
        if (d === 'D' && args?.[1]) {
            const parts = String(args[1]).split(',').map(Number);
            const w = parts[0], h = parts[1];
            if (!(w > 0) || !(h > 0)) return 'RECTANGLE D: usage — RECT D <width>,<height>.';
            runInteraction({
                steps: [PT('RECTANGLE: first corner')],
                complete: (pts) => {
                    const a = pts[0];
                    const fr = useSystemVarsStore.getState().FILLETRADIUS ?? 0;
                    const b: [number, number, number] = [a.x + w, a.y + h, a.z];
                    if (fr > 1e-4) {
                        const r = Math.min(fr, w / 2 - 1e-4, h / 2 - 1e-4);
                        const corners: [number, number][] = [[a.x, a.y], [a.x + w, a.y], [a.x + w, a.y + h], [a.x, a.y + h]];
                        const verts: number[][] = [];
                        const bulges: number[] = [];
                        const half = r / Math.SQRT2;
                        const bg = Math.tan((Math.PI / 2) / 4);
                        for (let i = 0; i < 4; i++) {
                            const [cx, cy] = corners[i];
                            const [px, py] = corners[(i + 3) % 4];
                            const [nx, ny] = corners[(i + 1) % 4];
                            const d1 = Math.hypot(cx - px, cy - py) || 1;
                            const d2 = Math.hypot(nx - cx, ny - cy) || 1;
                            verts.push([cx + (px - cx) / d1 * half, cy + (py - cy) / d1 * half, a.z]);
                            bulges.push(bg);
                            verts.push([cx + (nx - cx) / d2 * half, cy + (ny - cy) / d2 * half, a.z]);
                            bulges.push(0);
                        }
                        undoableAdd([makeEntity('polyline', `Rectangle-${Date.now()}`, {
                            vertices: verts, bulges, closed: true, rectFillet: r,
                        })]);
                        return `Rectangle ${w}x${h} with fillet ${r.toFixed(3)} placed.`;
                    }
                    undoableAdd([makeEntity('rectangle', `Rectangle-${Date.now()}`, {
                        corner1: [a.x, a.y, a.z], corner2: b,
                    })]);
                    return `Rectangle ${w}x${h} placed.`;
                },
            });
            return `RECTANGLE D ${w}x${h}: specify first corner`;
        }
        useAppStore.getState().setActiveTool('rectangle');
        return 'RECTANGLE tool activated (options: RECT D <w>,<h>; FILLETRADIUS rounds corners).';
    },

    // HELIX (Faz 7.5, OCS helix.rs): Center → base R → top R → Turns →
    // Height → Twist. turns ≤ 50 (AutoCAD limit, warning on reject).
    // `HELIX D` switches the radius prompts to diameter entry (session
    // toggle). Height 0 produces the 2D spiral as a `spline` entity; a
    // positive height samples a conical helix into an interpolating NURBS
    // curve that carries the helix data fields (3D view path).
    'draw.helix': (args) => {
        const d = (args?.[0] ?? '').toUpperCase();
        if (d === 'D' || d === 'DIAMETER') helixDiameterEntry = true;
        if (d === 'R' || d === 'RADIUS') helixDiameterEntry = false;
        runInteraction({
            steps: [
                PT('HELIX: specify center point'),
                PN(`HELIX: base ${helixDiameterEntry ? 'diameter' : 'radius'} <1.0> (HELIX D toggles)`, 1),
                PN(`HELIX: top ${helixDiameterEntry ? 'diameter' : 'radius'} <1.0>`, 1),
                PN('HELIX: number of turns <3>', 3),
                PN('HELIX: total height <0 (planar spiral)>', 0),
                PN('HELIX: twist 0=CCW / 1=CW <0>', 0),
            ],
            preview: rubberBand('#00ff00'),
            complete: (pts, values) => commitHelix(pts, values),
        });
        return `HELIX: specify center point (${helixDiameterEntry ? 'diameter' : 'radius'} entry)`;
    },

    'draw.spiral': () => {
        runInteraction({
            steps: [
                PT('SPIRAL: specify center point'),
                PN('SPIRAL: starting radius <0.5>'),
                PN('SPIRAL: ending radius <2.0>'),
                PN('SPIRAL: number of turns <3>'),
            ],
            complete: (pts, values) => {
                const points = spiralPoints(
                    pts[0].x, pts[0].y,
                    valueAtN(values, 1, 0.5), valueAtN(values, 2, 2),
                    Math.max(1, valueAtN(values, 3, 3)), pts[0].z
                );
                undoableAdd([nurbsCurveEntity(interpolateCurve(points) ?? curveFromControlPoints(points), `Spiral-${Date.now()}`)]);
                return `Spiral created (${points.length} samples, interpolating NURBS curve).`;
            },
        });
        return 'SPIRAL: specify center point';
    },

    'draw.xline': () => {
        runInteraction({
            steps: [PT('XLINE: first point'), PT('XLINE: second point')],
            preview: rubberBand('#888888'),
            complete: (pts) => {
                // Infinite construction line: stored as a very long segment.
                const a = pts[0], b = pts[1];
                const ext = (a.clone().sub(b).normalize().multiplyScalar(500));
                undoableAdd([makeEntity('line', `XLine-${Date.now()}`, {
                    start: [a.x - ext.x, a.y - ext.y, a.z], end: [a.x + ext.x, a.y + ext.y, a.z],
                    color: '#666666', xline: true,
                })]);
                return 'Construction line created.';
            },
        });
        return 'XLINE: first point';
    },

    'draw.ray': () => {
        runInteraction({
            steps: [PT('RAY: base point'), PT('RAY: through point')],
            preview: rubberBand('#888888'),
            complete: (pts) => {
                const a = pts[0], b = pts[1];
                const dir = b.clone().sub(a).normalize().multiplyScalar(1000);
                undoableAdd([makeEntity('line', `Ray-${Date.now()}`, {
                    start: [a.x, a.y, a.z], end: [a.x + dir.x, a.y + dir.y, a.z + dir.z],
                    color: '#666666', ray: true,
                })]);
                return 'Ray created.';
            },
        });
        return 'RAY: base point';
    },

    'draw.donut': () => {
        runInteraction({
            steps: [PN('DONUT: inside diameter <0.5>'), PN('DONUT: outside diameter <1.0>'),
                PLoop('DONUT: specify center point (Enter to finish)', 1)],
            complete: (pts, values) => {
                const di = Math.max(0.01, valueAtN(values, 0, 0.5));
                const doo = Math.max(di + 0.01, valueAtN(values, 1, 1));
                const created: string[] = [];
                pts.forEach((c) => {
                    const ring = donutMesh(di / 2, doo / 2, c.z);
                    const id = `cad:${crypto.randomUUID()}`;
                    created.push(id);
                    undoableAdd([solidEntity(ring, `Donut-${Date.now()}`, { closed: true })]);
                });
                return `${created.length} donut(s) created.`;
            },
        });
        return 'DONUT: specify center';
    },

    'draw.sketch': () => {
        runInteraction({
            steps: [{ prompt: 'SKETCH: move to draw, click or Enter to finish', kind: 'point', loop: true, minPoints: 2 }],
            collectOnMove: true,
            complete: (pts) => {
                if (pts.length < 2) return 'SKETCH: nothing drawn.';
                const cleaned = pts.filter((p, i, arr) => i === 0 || p.distanceTo(arr[i - 1]) > 1e-4);
                undoableAdd([curveEntity(
                    cleaned.map((p) => [p.x, p.y, p.z] as Pt3),
                    `Sketch-${Date.now()}`
                )]);
                return `Sketch captured (${cleaned.length} points).`;
            },
        });
        return 'SKETCH: move the cursor to draw';
    },

    'draw.mline': () => {
        const style = useMlineStyleStore.getState().styles[useMlineStyleStore.getState().activeId]
            ?? useMlineStyleStore.getState().styles['standard'];
        runInteraction({
            steps: [PN(`MLINE: width <${style.width}>`), PLoop('MLINE: specify points (Enter to finish)', 2)],
            preview: rubberBand('#00ff00'),
            complete: (pts, values) => {
                const w = Math.max(0.01, valueAtN(values, 0, style.width));
                const n = Math.max(2, Math.min(5, style.elements));
                const base = pts.map((p) => [p.x, p.y, p.z] as Pt3);
                // Equal spacing across the band, miter-joined per element.
                const created = Array.from({ length: n }, (_, i) => {
                    const off = n === 1 ? 0 : -w / 2 + (w * i) / (n - 1);
                    return curveEntity(offsetPathXY(base, off), `MLLine-${Date.now()}`);
                });
                undoableAdd(created);
                return `Multiline created (${n} elements, style "${style.name}").`;
            },
        });
        return 'MLINE: specify points';
    },

    'draw.boundary': () => {
        runInteraction({
            steps: [PK('BOUNDARY: pick a closed boundary curve')],
            complete: (_pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e) return 'BOUNDARY: invalid pick.';
                const poly = entityToPolyline(e);
                if (!poly || poly.length < 3) return 'BOUNDARY: selected object is not a closed loop.';
                undoableAdd([curveEntity(poly, `Boundary-${Date.now()}`, { closed: true })]);
                return `Boundary created from ${e.type} (${poly.length - 1} vertices).`;
            },
        });
        return 'BOUNDARY: pick a closed boundary';
    },

    'draw.wipeout': () => {
        runInteraction({
            steps: [PLoop('WIPEOUT: specify boundary corners (Enter to finish)', 3)],
            preview: rubberBand('#ff00ff'),
            complete: (pts) => {
                const ring = pts.map((p) => [p.x, p.y, p.z] as Pt3);
                const mesh = flatMesh(ring);
                // renderOrder: -1000 sorts the wipeout plate first, so the
                // EntityLayer draw-order pass paints it behind the linework.
                undoableAdd([makeEntity('solid', `Wipeout-${Date.now()}`, {
                    ...mesh, color: '#1e1e1e', wipeout: true, renderOrder: -1000,
                })]);
                return 'Wipeout created (draws behind lines).';
            },
        });
        return 'WIPEOUT: specify boundary corners';
    },

    'draw.solid2d': () => {
        runInteraction({
            steps: [PLoop('SOLID: specify polygon corners (Enter to finish)', 3)],
            preview: rubberBand('#00ff00'),
            complete: (pts) => {
                const ring = pts.map((p) => [p.x, p.y, p.z] as Pt3);
                const mesh = flatMesh(ring);
                undoableAdd([makeEntity('solid', `Solid2D-${Date.now()}`, { ...mesh, color: '#cccccc', flat: true })]);
                return `2D solid created (${ring.length - 1} corners).`;
            },
        });
        return 'SOLID: specify corners';
    },

    // GRADIENT (Faz 7.6): pick a closed boundary, then the dialog defines
    // the 9-pattern gradient (kind + inverted + 1/2 color + tint + angle +
    // centered) and commits the hatch entity on Apply.
    'draw.gradient': () => {
        runInteraction({
            steps: [PK('GRADIENT: pick a closed boundary')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e) return 'GRADIENT: invalid pick.';
                const poly = entityToPolyline(e);
                if (!poly || poly.length < 3) return 'GRADIENT: object is not a closed loop.';
                useGradientHatchDialogStore.getState().start(poly);
                return 'GRADIENT: define the fill in the dialog.';
            },
        });
        return 'GRADIENT: pick a boundary';
    },

    'draw.hatchEdit': () => {
        runInteraction({
            steps: [PK('HATCHEDIT: pick a fill'), PTxt('HATCHEDIT: new color (hex or name)')],
            complete: (_p, values, picks) => {
                const color = String(values[1] ?? '#cccccc').trim();
                undoableModify(picks, (d) => ({ ...d, color }));
                return `Fill color updated.`;
            },
        });
        return 'HATCHEDIT: pick a fill';
    },

    'draw.textStyle': () => {
        runInteraction({
            steps: [PTxt('TEXTSTYLE: new style name'), PN('TEXTSTYLE: font size <0.25>'), PN('TEXTSTYLE: line height <1.2>')],
            complete: (_p, values) => {
                const name = String(values[0] ?? `Style-${Date.now()}`).trim() || `Style-${Date.now()}`;
                const id = name.toLowerCase().replace(/\s+/g, '-');
                useTextStyleStore.getState().addStyle({
                    id, name, fontFamily: 'Inter', fontSize: valueAtN(values, 1, 0.25),
                    fontWeight: 'normal', fontStyle: 'normal', color: '#ffffff', alignment: 'left',
                    lineHeight: valueAtN(values, 2, 1.2),
                });
                useTextStyleStore.getState().setActiveStyle(id);
                return `Text style "${name}" created and set current.`;
            },
        });
        return 'TEXTSTYLE: enter name';
    },

    'draw.textObject': () => {
        runInteraction({
            steps: [PTxt('TEXT3D: enter text'), PN('TEXT3D: height <0.5>'), PN('TEXT3D: extrusion depth <0.05>'), PT('TEXT3D: insertion point')],
            complete: (pts, values) => {
                const text = String(values[0] ?? 'Text');
                const h = Math.max(0.05, valueAtN(values, 1, 0.5));
                const d = Math.max(0.01, valueAtN(values, 2, 0.05));
                const p = pts[0];
                // Real vector glyphs from the built-in stroke font: each
                // stroke segment extrudes into a solid ribbon box.
                const mesh = strokeTextToMesh(text, h, d);
                if (mesh.vertices.length === 0) {
                    return `TEXT3D: no drawable characters in "${text}".`;
                }
                undoableAdd([makeEntity('solid', `Text3D-${Date.now()}`, {
                    vertices: mesh.vertices.map((v) => [v[0] + p.x, v[1] + p.y, v[2] + p.z]),
                    indices: mesh.indices, color: '#e0c060', textObject: text,
                })]);
                return `3D text "${text}" created as an extruded vector solid.`;
            },
        });
        return 'TEXT3D: enter text';
    },

    'draw.mlineStyle': () => {
        runInteraction({
            steps: [PTxt('MLSTYLE: style name'), PN('MLSTYLE: number of lines <2>'), PN('MLSTYLE: total width <0.2>')],
            complete: (_p, values) => {
                const name = String(values[0] ?? 'Standard').trim();
                const id = name.toLowerCase().replace(/\s+/g, '-');
                useMlineStyleStore.getState().save({
                    id, name,
                    elements: Math.max(2, Math.min(5, Math.round(valueAtN(values, 1, 2)))),
                    width: Math.max(0.01, valueAtN(values, 2, 0.2)),
                    fill: false,
                });
                return `Multiline style "${name}" created and set current.`;
            },
        });
        return 'MLSTYLE: enter name';
    },

    'draw.mlineEdit': () => {
        runInteraction({
            steps: [PLoopK('MLEDIT: pick the two multiline elements to join (2 picks)', 2)],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const a = entities[picks[0]], b = entities[picks[1]];
                if (!a || !b) return 'MLEDIT: invalid picks.';
                const va = entityToPolyline(a), vb = entityToPolyline(b);
                if (!va || !vb || va.length < 2 || vb.length < 2) return 'MLEDIT: picks must be polyline elements.';
                // Weld: find the closest endpoint pair, rotate B so its near
                // end meets A's near end, chain the vertices into one element.
                const ends = (p: Pt3[]) => [[0, p[0]], [p.length - 1, p[p.length - 1]]] as [number, Pt3][];
                let best: { ai: number; bi: number; d: number } | null = null;
                for (const [ai, pa] of ends(va)) for (const [bi, pb] of ends(vb)) {
                    const d = Math.hypot(pa[0] - pb[0], pa[1] - pb[1]);
                    if (!best || d < best.d) best = { ai, bi, d };
                }
                if (!best) return 'MLEDIT: could not resolve endpoints.';
                const aFirst = best.ai === 0 ? va : [...va].reverse();
                const bChain = best.bi === 0 ? vb : [...vb].reverse();
                const joined = [...aFirst, ...bChain.slice(1)];
                // No color write when the source has none: rendering falls
                // back to the theme ink (white on dark, black on light).
                undoableReplace(picks, [curveEntity(joined, `MLLine-${Date.now()}`, {
                    ...(a.data.color ? { color: a.data.color as string } : {}),
                })]);
                return `MLEDIT: joined into one ${joined.length}-vertex element.`;
            },
        });
        return 'MLEDIT: pick two multilines';
    },

    // Draw order: bring selected objects to front/back via a render-order flag.
    'order.draw': (args) => {
        const mode = (args?.[0] ?? 'front').toLowerCase();
        const list = requireSelection('DRAWORDER');
        if (!list) return 'DRAWORDER: nothing selected.';
        const ids = list.map((e) => e.id);
        undoableModify(ids, (d, e) => ({
            ...d,
            renderOrder: mode.startsWith('back') ? -1000 : mode.startsWith('under') ? -10 : mode.startsWith('above') ? 10 : 1000,
        }));
        return `Draw order set to "${mode}" for ${ids.length} object(s).`;
    },
    // CIRCLE with a variant argument (2P / 3P) runs the classic two/three
    // point construction; TTR resolves tangency loci. Bare CIRCLE starts the
    // center-radius interaction with the variant chips (Faz 4.6): a chip or
    // typed keyword branches through spec.onOption into the variant flows —
    // the single keyword path (no prompt-text re-parsing).
    'draw.circle': (args) => {
        const variant = (args?.[0] ?? '').toUpperCase();
        if (variant === '2P') { startCircle2P(); return 'CIRCLE 2P: specify first point'; }
        if (variant === '3P') { startCircle3P(); return 'CIRCLE 3P: specify first point'; }
        if (variant === 'TTR') { startCircleTTR(Number(args?.[1]) > 0 ? Number(args?.[1]) : undefined); return 'CIRCLE TTR: pick two tangent objects (radius: CIRCLE TTR <r>, default 0.5)'; }
        runInteraction({
            steps: [
                {
                    prompt: 'CIRCLE: specify center point or [3P/2P/Ttr]:',
                    kind: 'point',
                    options: [{ keyword: '3P' }, { keyword: '2P' }, { keyword: 'Ttr' }],
                },
                PN('CIRCLE: specify radius of circle <1.0>', 1),
            ],
            preview: rubberBand('#00ff00'),
            onOption: (keyword) => {
                // Branch: restart the interaction as the picked variant.
                useCommandInteract.getState().cancelActive();
                if (keyword === '2P') startCircle2P();
                else if (keyword === '3P') startCircle3P();
                else startCircleTTR();
            },
            complete: (pts, values) => {
                if (pts.length < 1) return 'CIRCLE: no center point.';
                const c = pts[0];
                const r = Math.max(0.01, valueAtN(values, 1, 1));
                undoableAdd([makeEntity('circle', `Circle-${Date.now()}`, { center: [c.x, c.y, c.z], radius: r, segments: 64 })]);
                return `Circle r=${r.toFixed(3)}.`;
            },
        });
        return 'CIRCLE: specify center point or [3P/2P/Ttr]:';
    },
};

/** CIRCLE 2P: two points on the circle define center + radius. */
function startCircle2P(): void {
    runInteraction({
        steps: [PT('CIRCLE 2P: first point on circle'), PT('CIRCLE 2P: second point on circle')],
        complete: (pts) => {
            const [a, b] = pts;
            const c: Pt3 = [(a.x + b.x) / 2, (a.y + b.y) / 2, a.z];
            const r = Math.hypot(b.x - a.x, b.y - a.y) / 2;
            if (r < 1e-4) return 'CIRCLE 2P: points coincide.';
            undoableAdd([makeEntity('circle', `Circle-${Date.now()}`, { center: c, radius: r, segments: 64 })]);
            return `Circle (2P) r=${r.toFixed(3)}.`;
        },
    });
}

/** CIRCLE 3P: three points resolve the circumscribed circle. */
function startCircle3P(): void {
    runInteraction({
        steps: [PT('CIRCLE 3P: first point'), PT('CIRCLE 3P: second point'), PT('CIRCLE 3P: third point')],
        complete: (pts) => {
            const [p1, p2, p3] = pts;
            const d = 2 * (p1.x * (p2.y - p3.y) + p2.x * (p3.y - p1.y) + p3.x * (p1.y - p2.y));
            if (Math.abs(d) < 1e-6) return 'CIRCLE 3P: points are collinear.';
            const ux = ((p1.x ** 2 + p1.y ** 2) * (p2.y - p3.y) + (p2.x ** 2 + p2.y ** 2) * (p3.y - p1.y) + (p3.x ** 2 + p3.y ** 2) * (p1.y - p2.y)) / d;
            const uy = ((p1.x ** 2 + p1.y ** 2) * (p3.x - p2.x) + (p2.x ** 2 + p2.y ** 2) * (p1.x - p3.x) + (p3.x ** 2 + p3.y ** 2) * (p2.x - p1.x)) / d;
            const r = Math.hypot(p1.x - ux, p1.y - uy);
            undoableAdd([makeEntity('circle', `Circle-${Date.now()}`, { center: [ux, uy, p1.z], radius: r, segments: 64 })]);
            return `Circle (3P) r=${r.toFixed(3)}.`;
        },
    });
}

/** CIRCLE TTR: center must sit at distance r from both tangented objects —
 *  the locus is two offset lines (lines/arcs' first segment) or a concentric
 *  circle (R ± r). Candidate centers = locus intersections. */
function startCircleTTR(rr = 0.5): void {
    runInteraction({
        steps: [
            PK('CIRCLE TTR: pick the first tangent object (line or circle/arc)'),
            PK('CIRCLE TTR: pick the second tangent object'),
        ],
        complete: (_p, _v, picks) => {
            const { entities } = useAppStore.getState();
            const a = entities[picks[0]], b = entities[picks[1]];
            if (!a || !b) return 'CIRCLE TTR: invalid picks.';
            const loci = (e: typeof a): Array<{ kind: 'line'; a: Pt3; b: Pt3 } | { kind: 'circle'; c: Pt3; rad: number }> => {
                const poly = entityToPolyline(e);
                const d = e.data as Record<string, any>;
                const out: Array<{ kind: 'line'; a: Pt3; b: Pt3 } | { kind: 'circle'; c: Pt3; rad: number }> = [];
                if (e.type === 'circle' && Array.isArray(d.center) && typeof d.radius === 'number') {
                    out.push({ kind: 'circle', c: [d.center[0], d.center[1], d.center[2] ?? 0], rad: d.radius + rr });
                    out.push({ kind: 'circle', c: [d.center[0], d.center[1], d.center[2] ?? 0], rad: Math.abs(d.radius - rr) });
                } else if (poly && poly.length >= 2) {
                    const segs: Array<{ a: Pt3; b: Pt3 }> = [];
                    for (let i = 0; i < poly.length - 1; i++) segs.push({ a: poly[i], b: poly[i + 1] });
                    const s = segs[0];
                    const dx = s.b[0] - s.a[0], dy = s.b[1] - s.a[1];
                    const l = Math.hypot(dx, dy);
                    if (l < 1e-9) return out;
                    const px = -dy / l * rr, py = dx / l * rr;
                    out.push({ kind: 'line', a: [s.a[0] + px, s.a[1] + py, s.a[2]], b: [s.b[0] + px, s.b[1] + py, s.b[2]] });
                    out.push({ kind: 'line', a: [s.a[0] - px, s.a[1] - py, s.a[2]], b: [s.b[0] - px, s.b[1] - py, s.b[2]] });
                }
                return out;
            };
            const inter = (l1: ReturnType<typeof loci>[0], l2: ReturnType<typeof loci>[0]): Pt3[] => {
                if (l1.kind === 'line' && l2.kind === 'line') {
                    const p = lineIntersection2({ a: l1.a, b: l1.b }, { a: l2.a, b: l2.b });
                    return p ? [p] : [];
                }
                if (l1.kind === 'circle' && l2.kind === 'circle') {
                    return circleIntersections2({ center: l1.c, radius: l1.rad }, { center: l2.c, radius: l2.rad });
                }
                const lc = l1.kind === 'line' ? l1 : (l2 as typeof l1 & { kind: 'line' });
                const cc = l1.kind === 'circle' ? l1 : (l2 as typeof l1 & { kind: 'circle' });
                // Line vs circle locus: intersect the offset segment's
                // supporting line with the circle (sampled via closest
                // point + half-chord construction).
                const dx = lc.b[0] - lc.a[0], dy = lc.b[1] - lc.a[1];
                const len = Math.hypot(dx, dy);
                if (len < 1e-9) return [];
                const fx = cc.c[0] - lc.a[0], fy = cc.c[1] - lc.a[1];
                const along = (fx * dx + fy * dy) / (len * len);
                const cx = lc.a[0] + dx * along, cy = lc.a[1] + dy * along;
                const dPerp = Math.hypot(cc.c[0] - cx, cc.c[1] - cy);
                if (dPerp > cc.rad + 1e-9) return [];
                const half = Math.sqrt(Math.max(0, cc.rad * cc.rad - dPerp * dPerp));
                const ux = dx / len, uy = dy / len;
                return [
                    [cx + ux * half, cy + uy * half, cc.c[2]] as Pt3,
                    [cx - ux * half, cy - uy * half, cc.c[2]] as Pt3,
                ].filter((p, i) => i === 0 || Math.hypot(p[0] - (cx + ux * half), p[1] - (cy + uy * half)) > 1e-6);
            };
            const LA = loci(a), LB = loci(b);
            if (LA.length === 0 || LB.length === 0) return 'CIRCLE TTR: picks must be lines or circles/arcs.';
            let best: Pt3 | null = null;
            for (const l1 of LA) for (const l2 of LB) {
                const hits = inter(l1, l2);
                if (hits.length && !best) best = hits[0];
                if (hits.length && best && Math.hypot(hits[0][0] - best[0], hits[0][1] - best[1]) > 1e-6) {
                    // two solutions exist; keep the first, honor parity
                    break;
                }
            }
            if (!best) return 'CIRCLE TTR: no tangent circle of this radius fits between the objects.';
            undoableAdd([makeEntity('circle', `Circle-${Date.now()}`, { center: best, radius: rr, segments: 64 })]);
            return `Circle TTR r=${rr} tangent to both objects.`;
        },
    });
}

function valueAtN(values: Record<number, number | string>, i: number, fb: number): number {
    const v = values[i];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && Number.isFinite(Number(v))) return Number(v);
    return fb;
}

/**
 * Parallel offset of a vertex chain with MITER joins (MLINE corners):
 * the offset segments of adjacent chords intersect; the join vertex is that
 * intersection (clamped for degenerate angles), giving proper corner
 * closure instead of two independently shifted points.
 */
function offsetPathXY(pts: Pt3[], dist: number): Pt3[] {
    if (pts.length < 2) return pts;
    const normal = (a: Pt3, b: Pt3): [number, number] => {
        const dx = b[0] - a[0], dy = b[1] - a[1];
        const l = Math.hypot(dx, dy) || 1;
        return [-dy / l, dx / l];
    };
    // Offset each segment to a full line, then intersect consecutive lines.
    const lineOf = (a: Pt3, b: Pt3) => {
        const [nx, ny] = normal(a, b);
        return {
            a: [a[0] + nx * dist, a[1] + ny * dist, a[2]] as Pt3,
            b: [b[0] + nx * dist, b[1] + ny * dist, b[2]] as Pt3,
        };
    };
    const out: Pt3[] = [];
    for (let i = 0; i < pts.length; i++) {
        if (i === 0) {
            const [nx, ny] = normal(pts[0], pts[1]);
            out.push([pts[0][0] + nx * dist, pts[0][1] + ny * dist, pts[0][2]]);
            continue;
        }
        if (i === pts.length - 1) {
            const [nx, ny] = normal(pts[i - 1], pts[i]);
            out.push([pts[i][0] + nx * dist, pts[i][1] + ny * dist, pts[i][2]]);
            continue;
        }
        const l1 = lineOf(pts[i - 1], pts[i]);
        const l2 = lineOf(pts[i], pts[i + 1]);
        const x = lineIntersection2(l1, l2);
        if (x) {
            out.push(x);
        } else {
            // Collinear/parallel: simple normal offset of the vertex.
            const [nx, ny] = normal(pts[i - 1], pts[i]);
            out.push([pts[i][0] + nx * dist, pts[i][1] + ny * dist, pts[i][2]]);
        }
    }
    return out;
}

function donutMesh(inner: number, outer: number, z: number) {
    const seg = 48;
    const vertices: number[][] = [];
    const indices: number[][] = [];
    for (let i = 0; i <= seg; i++) {
        const a = (i / seg) * Math.PI * 2;
        vertices.push([Math.cos(a) * inner, Math.sin(a) * inner, z]);
        vertices.push([Math.cos(a) * outer, Math.sin(a) * outer, z]);
    }
    for (let i = 0; i < seg; i++) {
        const a = i * 2, b = a + 1, c = a + 2, d = a + 3;
        indices.push([a, b, d], [a, d, c]);
    }
    return { vertices, indices };
}

// ---------------- SPLINE fit/CV flows (Faz 7.2) ----------------

/** Session-remembered option state (OCS remembers settings per session). */
let splineKnots: KnotParameterization = 0;
let splineCvDegree = 3;
/** HELIX D toggle: radius prompts read diameters (Faz 7.5). */
let helixDiameterEntry = false;
/** AutoCAD HELIX turn limit. */
const HELIX_MAX_TURNS = 50;

/**
 * Sample a conical helix (base/top radius, handedness) into world points.
 * Height 0 degrades to the planar spiral (OCS helix.rs 2D behavior).
 */
function conicalHelixPoints(
    center: Pt3, rBase: number, rTop: number, turns: number, height: number, cw: boolean
): Pt3[] {
    const perTurn = 48;
    const total = Math.max(1, Math.round(turns * perTurn));
    const pts: Pt3[] = [];
    const dir = cw ? -1 : 1;
    for (let i = 0; i <= total; i++) {
        const t = i / total;
        const ang = dir * 2 * Math.PI * turns * t;
        const r = rBase + (rTop - rBase) * t;
        pts.push([
            center[0] + Math.cos(ang) * r,
            center[1] + Math.sin(ang) * r,
            (center[2] ?? 0) + height * t,
        ]);
    }
    return pts;
}

/** HELIX commit: turn gate, 2D spiral → spline / 3D conical helix → NURBS. */
function commitHelix(pts: THREE.Vector3[], values: Record<number, number | string>): string {
    const scale = helixDiameterEntry ? 0.5 : 1;
    const c: Pt3 = [pts[0].x, pts[0].y, pts[0].z];
    const rBase = Math.max(0.001, valueAtN(values, 1, 1) * scale);
    const rTop = Math.max(0.001, valueAtN(values, 2, rBase) * scale);
    const turns = valueAtN(values, 3, 3);
    const height = valueAtN(values, 4, 0);
    const cw = Math.round(valueAtN(values, 5, 0)) === 1;
    if (turns < 0.001) return 'HELIX: number of turns must be positive.';
    if (turns > HELIX_MAX_TURNS) {
        return `HELIX: ${turns} turns rejected — the value must be between 0.001 and ${HELIX_MAX_TURNS} (AutoCAD limit).`;
    }
    if (height <= 1e-9) {
        // Planar spiral → the Faz 7.2 spline entity (fit source: the curve
        // passes through the sampled spiral exactly, KNOTS Chord).
        const spiral = conicalHelixPoints(c, rBase, rTop, turns, 0, cw);
        undoableAdd([splineEntity({ fitPoints: spiral }, `Helix-${Date.now()}`, { knotParameterization: 0 })]);
        return `Planar helix spiral created: ${turns} turns, ${cw ? 'CW' : 'CCW'} (spline entity).`;
    }
    const helix = conicalHelixPoints(c, rBase, rTop, turns, height, cw);
    const curve = interpolateCurve(helix) ?? curveFromControlPoints(helix);
    const entity = nurbsCurveEntity(curve, `Helix-${Date.now()}`);
    entity.data.turns = turns;
    entity.data.turnHeight = height / turns;
    entity.data.handedness = cw ? 'CW' : 'CCW';
    entity.data.axisVector = [0, 0, height];
    undoableAdd([entity]);
    return `Helix created: ${turns} turns, height ${height.toFixed(3)}, ${cw ? 'CW' : 'CCW'} (${helix.length} samples).`;
}

/** Commit a fit-mode spline from collected points. */
function commitSplineFit(points: THREE.Vector3[], closed: boolean): string {
    const fitPoints = points.map((p) => [p.x, p.y, p.z] as Pt3);
    if (fitPoints.length < 2) return 'SPLINE: at least two fit points required.';
    undoableAdd([splineEntity({ fitPoints }, `Spline-${Date.now()}`, {
        knotParameterization: splineKnots, closed,
    })]);
    return `Spline created through ${fitPoints.length} fit point(s)`
        + (closed ? ' (closed)' : '') + ` (KNOTS ${['Chord', 'Sqrt', 'Uniform'][splineKnots]}).`;
}

/** Commit a CV-mode spline from collected control points. */
function commitSplineCv(points: THREE.Vector3[], closed: boolean): string {
    const controlPoints = points.map((p) => [p.x, p.y, p.z] as Pt3);
    const degree = Math.max(1, Math.min(splineCvDegree, controlPoints.length - 1));
    if (controlPoints.length < 2) return 'SPLINECV: at least two control points required.';
    undoableAdd([splineEntity({ controlPoints }, `Spline-${Date.now()}`, {
        degree, closed,
    })]);
    return `CV spline created: ${controlPoints.length} control point(s), degree ${degree}`
        + (closed ? ' (closed)' : '') + '.';
}

/** SPLINE fit-point flow: [Object/Knots/Close] options + rubber band. */
function startSplineFit(): void {
    runInteraction({
        steps: [{
            prompt: 'SPLINE: specify first fit point or [Object/Knots/Close]:',
            kind: 'point',
            loop: true,
            minPoints: 2,
            options: [{ keyword: 'Object' }, { keyword: 'Knots' }, { keyword: 'Close' }],
        }],
        preview: rubberBand('#00ff00'),
        onOption: (keyword) => {
            const ci = useCommandInteract.getState();
            if (keyword === 'OBJECT') {
                ci.cancelActive();
                runInteraction({
                    steps: [PK('SPLINE Object: pick a curve or polyline to convert', ['polyline', 'curve', 'spline', 'nurbs-curve', 'rectangle', 'polygon'])],
                    complete: (_p, _v, picks) => {
                        const e = useAppStore.getState().entities[picks[0]];
                        const poly = e ? entityToPolyline(e) : null;
                        if (!poly || poly.length < 2) return 'SPLINE Object: pick has no curve geometry.';
                        undoableAdd([splineEntity({ fitPoints: poly.slice() }, `Spline-${Date.now()}`, {
                            knotParameterization: splineKnots,
                        })]);
                        return `Spline fitted through ${poly.length} converted point(s).`;
                    },
                });
                return;
            }
            if (keyword === 'KNOTS') {
                ci.cancelActive();
                runInteraction({
                    steps: [PN('SPLINE Knots: parameterization 0=Chord / 1=Sqrt / 2=Uniform <0>', 0)],
                    complete: (_p, values) => {
                        const k = Math.round(valueAtN(values, 0, 0));
                        splineKnots = (k === 1 || k === 2 ? k : 0) as KnotParameterization;
                        startSplineFit();
                        return `KNOTS parameterization set to ${['Chord', 'Sqrt', 'Uniform'][splineKnots]}.`;
                    },
                });
                return;
            }
            if (keyword === 'CLOSE') {
                const collected = ci.collected;
                if (collected.length < 3) return;
                ci.cancelActive();
                commitSplineFit(collected, true);
            }
        },
        complete: (points) => commitSplineFit(points, false),
    });
}

/** SPLINECV control-vertex flow: [Degree/Close] options + rubber band. */
function startSplineCv(): void {
    runInteraction({
        steps: [{
            prompt: 'SPLINECV: specify first control point or [Degree/Close]:',
            kind: 'point',
            loop: true,
            minPoints: 2,
            options: [{ keyword: 'Degree' }, { keyword: 'Close' }],
        }],
        preview: rubberBand('#00ff00'),
        onOption: (keyword) => {
            const ci = useCommandInteract.getState();
            if (keyword === 'DEGREE') {
                ci.cancelActive();
                runInteraction({
                    steps: [PN('SPLINECV Degree: curve degree (CV mode only) <3>', 3)],
                    complete: (_p, values) => {
                        splineCvDegree = Math.max(1, Math.round(valueAtN(values, 0, 3)));
                        startSplineCv();
                        return `Spline degree set to ${splineCvDegree}.`;
                    },
                });
                return;
            }
            if (keyword === 'CLOSE') {
                const collected = ci.collected;
                if (collected.length < 3) return;
                ci.cancelActive();
                commitSplineCv(collected, true);
            }
        },
        complete: (points) => commitSplineCv(points, false),
    });
}
